import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEstablishment } from '../components/EstablishmentSelector';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Alert,
    AlertDescription,
} from '../components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
    Plus, 
    ChevronLeft, 
    ChevronRight,
    Calendar,
    Users,
    Clock,
    Edit2,
    Trash2,
    RefreshCw,
    Sun,
    Moon,
    User,
    Building2,
    Timer,
    Copy,
    CalendarDays,
    AlertCircle,
    Eye,
    Pencil,
    Info,
    Lock
} from 'lucide-react';
import ShiftClosureDialog from '../components/ShiftClosureDialog';
import { 
    GABON_TIMEZONE, 
    getGabonNow, 
    getGabonTodayISO, 
    getGabonDayOfWeek,
    toGabonDate,
    getDayOfWeekFromDateString,
    getDayFromDateString
} from '../utils/gabonTime';

const SERVICE_COLORS = {
    'Direction': { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700', badge: 'bg-purple-500' },
    'Cuisine': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700', badge: 'bg-orange-500' },
    'Salle': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', badge: 'bg-blue-500' },
    'Bar': { bg: 'bg-pink-100', border: 'border-pink-300', text: 'text-pink-700', badge: 'bg-pink-500' },
    'default': { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700', badge: 'bg-gray-500' }
};

const DAY_NAMES = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const FULL_DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

const PlanningPage = () => {
    const { api, hasPermission, user } = useAuth();
    const { selectedEstablishmentId } = useEstablishment();
    const [loading, setLoading] = useState(true);
    const [weekData, setWeekData] = useState(null);
    const [staff, setStaff] = useState([]);
    const [services, setServices] = useState([]);
    const [positions, setPositions] = useState({});
    
    const [currentDate, setCurrentDate] = useState(getGabonNow());
    const [selectedService, setSelectedService] = useState('');
    const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
    const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
        const gabonDay = getGabonDayOfWeek();
        return gabonDay === 0 ? 6 : gabonDay - 1;
    });
    
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [editSchedule, setEditSchedule] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    
    // Gestion des doublons
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    
    // Clôture de shift
    const [showCloseDialog, setShowCloseDialog] = useState(false);
    const [closingDate, setClosingDate] = useState(null);
    
    // Création rapide en lot
    const [showBulkDialog, setShowBulkDialog] = useState(false);
    const [bulkData, setBulkData] = useState({
        selectedStaff: [],
        selectedDays: [],
        start_time: '09:00',
        end_time: '17:00'
    });
    
    const [formData, setFormData] = useState({
        staff_id: '',
        start_time: '09:00',
        end_time: '17:00',
        service: '',
        position: '',
        notes: ''
    });
    
    const canCreate = hasPermission('create_schedule');
    const canEdit = hasPermission('edit_schedule');
    const canDelete = hasPermission('delete_schedule');
    const canCloseShift = hasPermission('edit_shift');

    // Fonction pour ouvrir le dialog de clôture
    const handleOpenCloseDialog = (date) => {
        setClosingDate(date);
        setShowCloseDialog(true);
    };

    const getWeekDates = useCallback((date) => {
        // Convertir la date en date Gabon pour s'assurer du bon jour
        const d = toGabonDate(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(d.setDate(diff));
        
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const current = new Date(monday);
            current.setDate(monday.getDate() + i);
            // Formater la date en YYYY-MM-DD sans passer par UTC
            const year = current.getFullYear();
            const month = String(current.getMonth() + 1).padStart(2, '0');
            const dayNum = String(current.getDate()).padStart(2, '0');
            dates.push(`${year}-${month}-${dayNum}`);
        }
        return dates;
    }, []);

    const weekDates = getWeekDates(currentDate);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            // Utiliser une date locale sans conversion UTC
            const year = currentDate.getFullYear();
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const day = String(currentDate.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            
            // Debug sans weekDates pour éviter la boucle infinie
            console.log('[Planning] Fetching data for:', dateStr);
            
            // Construire les paramètres de requête
            const params = new URLSearchParams();
            if (selectedService) params.append('service', selectedService);
            // Note: On ne filtre plus par établissement pour afficher tous les shifts
            const queryString = params.toString() ? `?${params.toString()}` : '';
            
            const [weekRes, staffRes, servicesRes] = await Promise.all([
                api().get(`/schedules/week/${dateStr}${queryString}`),
                api().get('/staff/names'),
                api().get('/services')
            ]);
            
            console.log('[Planning] API Response:', {
                week_start: weekRes.data?.week_start,
                week_end: weekRes.data?.week_end,
                schedules_count: weekRes.data?.schedules?.length || 0,
                by_day_keys: Object.keys(weekRes.data?.by_day || {})
            });
            
            setWeekData(weekRes.data);
            setStaff(staffRes.data || []);
            setServices(servicesRes.data?.services || []);
            setPositions(servicesRes.data?.positions || {});
        } catch (error) {
            console.error('[Planning] Fetch error:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [api, currentDate, selectedService]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const handlePrevWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentDate(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentDate(newDate);
    };

    const handleToday = () => {
        setCurrentDate(getGabonNow());
        const today = getGabonDayOfWeek();
        setSelectedDayIndex(today === 0 ? 6 : today - 1);
    };

    const handleAddClick = (dayDate) => {
        console.log('[Planning] Add click for:', dayDate);
        
        setSelectedDay(dayDate);
        setFormData({
            staff_id: '',
            start_time: '09:00',
            end_time: '17:00',
            service: '',
            position: '',
            notes: ''
        });
        setEditSchedule(null);
        setShowAddDialog(true);
    };
    
    // Vérifier si l'employé a déjà un shift ce jour-là
    const checkExistingShift = useCallback((staffId, date) => {
        if (!weekData?.by_day?.[date]) return null;
        return weekData.by_day[date].find(s => s.staff_id === staffId);
    }, [weekData]);
    
    // Quand on sélectionne un employé, vérifier s'il a déjà un shift
    const handleStaffSelect = (staffId) => {
        const selectedStaff = staff.find(s => s.id === staffId);
        const existingShift = checkExistingShift(staffId, selectedDay);
        
        console.log('[Planning] Staff selected:', { staffId, selectedDay, hasExistingShift: !!existingShift });
        
        if (existingShift && !editSchedule) {
            // L'employé a déjà un shift ce jour-là
            setDuplicateInfo({
                schedule: existingShift,
                date: selectedDay,
                staffName: selectedStaff?.full_name || 'Employé'
            });
            setShowAddDialog(false);
            setShowDuplicateDialog(true);
        } else {
            setFormData(prev => ({
                ...prev,
                staff_id: staffId,
                service: selectedStaff?.service || prev.service,
                position: selectedStaff?.position || prev.position
            }));
        }
    };

    const handleEditClick = (schedule) => {
        setEditSchedule(schedule);
        setSelectedDay(schedule.date);
        setFormData({
            staff_id: schedule.staff_id,
            start_time: schedule.start_time,
            end_time: schedule.end_time,
            service: schedule.service,
            position: schedule.position,
            notes: schedule.notes || ''
        });
        setShowAddDialog(true);
    };

    const handleSave = async () => {
        if (!formData.staff_id) {
            toast.error('Sélectionnez un employé');
            return;
        }
        
        console.log('[Planning] Saving schedule:', { formData, selectedDay, editId: editSchedule?.id });
        
        try {
            if (editSchedule) {
                await api().put(`/schedules/${editSchedule.id}`, {
                    start_time: formData.start_time,
                    end_time: formData.end_time,
                    service: formData.service,
                    position: formData.position,
                    notes: formData.notes
                });
                toast.success('Shift modifié');
                setShowAddDialog(false);
                await fetchData(); // Rafraîchir immédiatement
            } else {
                const response = await api().post('/schedules', {
                    staff_id: formData.staff_id,
                    date: selectedDay,
                    start_time: formData.start_time,
                    end_time: formData.end_time,
                    service: formData.service,
                    position: formData.position,
                    notes: formData.notes,
                    establishment_id: null // Ne pas filtrer par établissement
                });
                console.log('[Planning] Schedule created:', response.data);
                toast.success('Shift créé avec succès');
                setShowAddDialog(false);
                await fetchData(); // Rafraîchir immédiatement
            }
        } catch (error) {
            const errorDetail = error.response?.data?.detail || '';
            console.error('[Planning] Save error:', errorDetail);
            
            // Gérer le cas du doublon
            if (errorDetail.includes('existe déjà') || errorDetail.includes('already exists')) {
                // Chercher le shift existant
                const existingSchedule = weekData?.schedules?.find(
                    s => s.staff_id === formData.staff_id && s.date === selectedDay
                ) || weekData?.by_day?.[selectedDay]?.find(
                    s => s.staff_id === formData.staff_id
                );
                
                if (existingSchedule) {
                    setDuplicateInfo({
                        schedule: existingSchedule,
                        date: selectedDay,
                        staffName: staff.find(s => s.id === formData.staff_id)?.full_name || 'Employé'
                    });
                    setShowDuplicateDialog(true);
                } else {
                    // Si on ne trouve pas le shift, proposer de rafraîchir
                    toast.error(
                        <div>
                            <p>Un shift existe déjà pour cet employé à cette date.</p>
                            <Button 
                                size="sm" 
                                variant="outline" 
                                className="mt-2"
                                onClick={() => {
                                    fetchData();
                                    toast.dismiss();
                                }}
                            >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                Rafraîchir le planning
                            </Button>
                        </div>,
                        { duration: 10000 }
                    );
                }
            } else {
                toast.error(errorDetail || 'Erreur lors de la sauvegarde');
            }
        }
    };
    
    // Gérer les actions sur le doublon
    const handleDuplicateAction = async (action) => {
        if (!duplicateInfo) return;
        
        switch (action) {
            case 'view':
                // Fermer le dialogue et mettre en surbrillance le shift
                setShowDuplicateDialog(false);
                setShowAddDialog(false);
                toast.info(`Shift existant: ${duplicateInfo.schedule.start_time} - ${duplicateInfo.schedule.end_time}`);
                break;
                
            case 'edit':
                // Ouvrir le dialogue d'édition avec le shift existant
                setShowDuplicateDialog(false);
                setEditSchedule(duplicateInfo.schedule);
                setFormData({
                    staff_id: duplicateInfo.schedule.staff_id,
                    start_time: duplicateInfo.schedule.start_time,
                    end_time: duplicateInfo.schedule.end_time,
                    service: duplicateInfo.schedule.service,
                    position: duplicateInfo.schedule.position,
                    notes: duplicateInfo.schedule.notes || ''
                });
                break;
                
            case 'delete':
                if (canDelete) {
                    try {
                        await api().delete(`/schedules/${duplicateInfo.schedule.id}`);
                        toast.success('Shift supprimé');
                        setShowDuplicateDialog(false);
                        setDuplicateInfo(null);
                        await fetchData();
                    } catch (error) {
                        toast.error('Erreur lors de la suppression');
                    }
                }
                break;
                
            default:
                setShowDuplicateDialog(false);
        }
        setDuplicateInfo(null);
    };

    const handleDelete = async (scheduleId) => {
        if (!window.confirm('Supprimer ce shift ?')) return;
        
        try {
            await api().delete(`/schedules/${scheduleId}`);
            toast.success('Shift supprimé');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    // Création en lot
    const handleBulkCreate = async () => {
        if (bulkData.selectedStaff.length === 0) {
            toast.error('Sélectionnez au moins un employé');
            return;
        }
        if (bulkData.selectedDays.length === 0) {
            toast.error('Sélectionnez au moins un jour');
            return;
        }
        
        try {
            let created = 0;
            let errors = 0;
            
            for (const staffId of bulkData.selectedStaff) {
                const selectedStaff = staff.find(s => s.id === staffId);
                
                for (const date of bulkData.selectedDays) {
                    try {
                        await api().post('/schedules', {
                            staff_id: staffId,
                            date: date,
                            start_time: bulkData.start_time,
                            end_time: bulkData.end_time,
                            service: selectedStaff?.service || '',
                            position: selectedStaff?.position || '',
                            establishment_id: selectedEstablishmentId || null
                        });
                        created++;
                    } catch (e) {
                        errors++;
                    }
                }
            }
            
            if (created > 0) {
                toast.success(`${created} shift(s) créé(s)${errors > 0 ? ` (${errors} erreurs)` : ''}`);
            } else {
                toast.error('Aucun shift créé');
            }
            
            setShowBulkDialog(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la création');
        }
    };

    const toggleStaffSelection = (staffId) => {
        setBulkData(prev => ({
            ...prev,
            selectedStaff: prev.selectedStaff.includes(staffId)
                ? prev.selectedStaff.filter(id => id !== staffId)
                : [...prev.selectedStaff, staffId]
        }));
    };

    const toggleDaySelection = (date) => {
        setBulkData(prev => ({
            ...prev,
            selectedDays: prev.selectedDays.includes(date)
                ? prev.selectedDays.filter(d => d !== date)
                : [...prev.selectedDays, date]
        }));
    };

    const selectAllStaff = () => {
        const filteredStaff = selectedService 
            ? staff.filter(s => s.service === selectedService)
            : staff;
        setBulkData(prev => ({
            ...prev,
            selectedStaff: filteredStaff.map(s => s.id)
        }));
    };

    const selectAllDays = () => {
        setBulkData(prev => ({
            ...prev,
            selectedDays: weekDates.slice()
        }));
    };

    const getServiceColors = (service) => {
        return SERVICE_COLORS[service] || SERVICE_COLORS['default'];
    };

    const getStatusBadge = (status, isClosed = false) => {
        const configs = {
            'PENDING': { color: 'bg-slate-100 text-slate-600 border-slate-200', label: 'En attente', icon: null },
            'PRESENT': { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Présent', icon: '✓' },
            'LATE': { color: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Retard', icon: '⏰' },
            'ABSENT': { color: 'bg-red-100 text-red-700 border-red-200', label: 'Absent', icon: '✗' },
            'AUTO_ABSENT': { color: 'bg-red-50 text-red-600 border-red-200', label: 'Absent (auto)', icon: '⊘' },
            'NOT_CLOCKED': { color: 'bg-orange-100 text-orange-700 border-orange-200', label: 'Non pointé', icon: '?' }
        };
        const config = configs[status] || configs['PENDING'];
        return (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${config.color} ${isClosed ? 'opacity-70' : ''}`}>
                {config.icon && <span className="mr-0.5">{config.icon}</span>}
                {config.label}
                {isClosed && <Lock className="w-2 h-2 ml-0.5 inline-block" />}
            </span>
        );
    };

    const calculateHours = (start, end) => {
        const [sh, sm] = start.split(':').map(Number);
        const [eh, em] = end.split(':').map(Number);
        const totalMinutes = (eh * 60 + em) - (sh * 60 + sm);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return minutes > 0 ? `${hours}h${minutes}` : `${hours}h`;
    };

    // Calculer le temps restant d'un shift (pour afficher un indicateur de rappel)
    const getShiftTimeRemaining = (endTime, date) => {
        const today = getGabonTodayISO();
        if (date !== today) return null;
        
        const now = getGabonNow();
        const [eh, em] = endTime.split(':').map(Number);
        const endDate = getGabonNow();
        endDate.setHours(eh, em, 0, 0);
        
        const diffMinutes = Math.floor((endDate - now) / (1000 * 60));
        
        if (diffMinutes <= 0) return { status: 'ended', minutes: 0 };
        if (diffMinutes <= 15) return { status: 'ending_soon', minutes: diffMinutes };
        if (diffMinutes <= 60) return { status: 'within_hour', minutes: diffMinutes };
        return null;
    };

    // Stats calculation
    const getWeekStats = () => {
        if (!weekData?.by_day) return { total: 0, byService: {} };
        
        let total = 0;
        const byService = {};
        
        Object.values(weekData.by_day).forEach(daySchedules => {
            daySchedules.forEach(s => {
                total++;
                const service = s.staff_service || 'Autre';
                byService[service] = (byService[service] || 0) + 1;
            });
        });
        
        return { total, byService };
    };

    const stats = getWeekStats();

    if (loading && !weekData) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="planning-page">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl lg:text-3xl font-bold text-foreground tracking-tight flex items-center gap-2">
                        <Calendar className="w-7 h-7 text-primary" />
                        Planning
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Semaine du {new Date(weekDates[0]).toLocaleDateString('fr-FR', { timeZone: GABON_TIMEZONE, day: 'numeric', month: 'long' })} au {new Date(weekDates[6]).toLocaleDateString('fr-FR', { timeZone: GABON_TIMEZONE, day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                </div>
                
                {/* Stats badges */}
                <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                        <Users className="w-3 h-3 mr-1" />
                        {stats.total} shifts
                    </Badge>
                    {Object.entries(stats.byService).map(([service, count]) => (
                        <Badge 
                            key={service} 
                            variant="outline" 
                            className={`${getServiceColors(service).bg} ${getServiceColors(service).text} ${getServiceColors(service).border}`}
                        >
                            {service}: {count}
                        </Badge>
                    ))}
                </div>
            </div>

            {/* Navigation Bar */}
            <Card className="shadow-sm">
                <CardContent className="p-3 lg:p-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
                        {/* Week navigation */}
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={handlePrevWeek} className="h-9 w-9">
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleToday} className="h-9">
                                Aujourd'hui
                            </Button>
                            <Button variant="outline" size="icon" onClick={handleNextWeek} className="h-9 w-9">
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={fetchData} className="h-9 w-9">
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            
                            {/* Bouton création rapide */}
                            {canCreate && (
                                <Button 
                                    onClick={() => {
                                        setBulkData({
                                            selectedStaff: [],
                                            selectedDays: weekDates.slice(),
                                            start_time: '09:00',
                                            end_time: '17:00'
                                        });
                                        setShowBulkDialog(true);
                                    }}
                                    className="h-9 bg-gradient-to-r from-primary to-primary/80"
                                    data-testid="bulk-schedule-btn"
                                >
                                    <CalendarDays className="w-4 h-4 mr-2" />
                                    <span className="hidden sm:inline">Planning rapide</span>
                                    <span className="sm:hidden">Rapide</span>
                                </Button>
                            )}
                        </div>
                        
                        {/* Filters */}
                        <div className="flex items-center gap-2 w-full lg:w-auto">
                            <Select value={selectedService || "all"} onValueChange={(v) => setSelectedService(v === "all" ? "" : v)}>
                                <SelectTrigger className="w-full lg:w-40 h-9">
                                    <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                                    <SelectValue placeholder="Tous services" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous services</SelectItem>
                                    {services.map(s => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            
                            {/* View mode toggle - mobile only */}
                            <div className="lg:hidden flex items-center border rounded-lg p-1">
                                <Button 
                                    variant={viewMode === 'day' ? 'default' : 'ghost'} 
                                    size="sm" 
                                    className="h-7 px-3"
                                    onClick={() => setViewMode('day')}
                                >
                                    Jour
                                </Button>
                                <Button 
                                    variant={viewMode === 'week' ? 'default' : 'ghost'} 
                                    size="sm" 
                                    className="h-7 px-3"
                                    onClick={() => setViewMode('week')}
                                >
                                    Semaine
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Day selector for mobile day view */}
            {viewMode === 'day' && (
                <div className="lg:hidden">
                    <ScrollArea className="w-full">
                        <div className="flex gap-2 pb-2">
                            {weekDates.map((date, idx) => {
                                const dayOfWeek = getDayOfWeekFromDateString(date);
                                const dayNumber = getDayFromDateString(date);
                                const isToday = date === getGabonTodayISO();
                                const isSelected = idx === selectedDayIndex;
                                const daySchedules = weekData?.by_day?.[date] || [];
                                
                                return (
                                    <button
                                        key={date}
                                        onClick={() => setSelectedDayIndex(idx)}
                                        className={`flex-shrink-0 p-3 rounded-xl text-center min-w-[70px] transition-all ${
                                            isSelected 
                                                ? 'bg-primary text-primary-foreground shadow-lg scale-105' 
                                                : isToday 
                                                    ? 'bg-primary/10 border-2 border-primary/30' 
                                                    : 'bg-muted/50 hover:bg-muted'
                                        }`}
                                    >
                                        <div className="text-xs font-medium opacity-80">{DAY_NAMES[dayOfWeek]}</div>
                                        <div className="text-lg font-bold">{dayNumber}</div>
                                        {daySchedules.length > 0 && (
                                            <div className={`text-[10px] mt-1 ${isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                                                {daySchedules.length} shift{daySchedules.length > 1 ? 's' : ''}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </ScrollArea>
                </div>
            )}

            {/* Week Grid - Desktop */}
            <div className="hidden lg:block">
                <Card className="shadow-sm overflow-hidden">
                    <div className="grid grid-cols-7 border-b bg-muted/30">
                        {weekDates.map((date, idx) => {
                            const dayOfWeek = getDayOfWeekFromDateString(date);
                            const dayNumber = getDayFromDateString(date);
                            const isToday = date === getGabonTodayISO();
                            const daySchedules = weekData?.by_day?.[date] || [];
                            
                            return (
                                <div 
                                    key={date} 
                                    className={`p-3 text-center border-r last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}
                                >
                                    <div className={`text-xs font-medium ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                                        {DAY_NAMES[dayOfWeek]}
                                    </div>
                                    <div className={`text-xl font-bold mt-1 ${
                                        isToday 
                                            ? 'w-9 h-9 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto' 
                                            : ''
                                    }`}>
                                        {dayNumber}
                                    </div>
                                    {daySchedules.length > 0 && (
                                        <div className="text-[10px] text-muted-foreground mt-1">
                                            {daySchedules.length} shift{daySchedules.length > 1 ? 's' : ''}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    <div className="grid grid-cols-7 min-h-[400px]">
                        {weekDates.map((date, idx) => {
                            const daySchedules = weekData?.by_day?.[date] || [];
                            const isToday = date === getGabonTodayISO();
                            
                            return (
                                <div 
                                    key={date} 
                                    className={`p-2 border-r last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}
                                >
                                    <div className="space-y-2">
                                        {daySchedules.map(schedule => {
                                            const colors = getServiceColors(schedule.staff_service);
                                            const timeRemaining = getShiftTimeRemaining(schedule.end_time, date);
                                            return (
                                                <div 
                                                    key={schedule.id}
                                                    className={`p-2 rounded-lg border ${colors.bg} ${colors.border} transition-all hover:shadow-md group ${
                                                        timeRemaining?.status === 'ending_soon' ? 'ring-2 ring-amber-400 animate-pulse' : ''
                                                    }`}
                                                >
                                                    <div className={`font-semibold text-xs truncate ${colors.text}`}>
                                                        {schedule.staff_name}
                                                    </div>
                                                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
                                                        <Clock className="w-3 h-3" />
                                                        {schedule.start_time} - {schedule.end_time}
                                                        <span className="ml-1 opacity-60">
                                                            ({calculateHours(schedule.start_time, schedule.end_time)})
                                                        </span>
                                                    </div>
                                                    {/* Indicateur de temps restant */}
                                                    {timeRemaining && (
                                                        <div className={`text-[9px] mt-1 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 ${
                                                            timeRemaining.status === 'ending_soon' 
                                                                ? 'bg-amber-100 text-amber-700 font-medium'
                                                                : timeRemaining.status === 'ended'
                                                                ? 'bg-gray-100 text-gray-600'
                                                                : 'bg-blue-50 text-blue-600'
                                                        }`}>
                                                            {timeRemaining.status === 'ending_soon' && (
                                                                <>⏰ {timeRemaining.minutes}min</>
                                                            )}
                                                            {timeRemaining.status === 'ended' && (
                                                                <>✓ Terminé</>
                                                            )}
                                                            {timeRemaining.status === 'within_hour' && (
                                                                <>{timeRemaining.minutes}min restantes</>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center justify-between mt-2">
                                                        {getStatusBadge(schedule.attendance_status)}
                                                        {canEdit && (
                                                            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon" 
                                                                    className="h-5 w-5"
                                                                    onClick={() => handleEditClick(schedule)}
                                                                >
                                                                    <Edit2 className="w-3 h-3" />
                                                                </Button>
                                                                {canDelete && (
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="icon" 
                                                                        className="h-5 w-5 text-destructive hover:text-destructive"
                                                                        onClick={() => handleDelete(schedule.id)}
                                                                    >
                                                                        <Trash2 className="w-3 h-3" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        
                                        {canCreate && (
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="w-full h-8 border border-dashed hover:border-primary hover:bg-primary/5"
                                                onClick={() => handleAddClick(date)}
                                            >
                                                <Plus className="w-3 h-3 mr-1" />
                                                Ajouter
                                            </Button>
                                        )}
                                        
                                        {/* Bouton clôture - visible si shifts existent */}
                                        {canCloseShift && daySchedules.length > 0 && (
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="w-full h-7 text-xs hover:bg-amber-50 hover:text-amber-700 text-muted-foreground"
                                                onClick={() => handleOpenCloseDialog(date)}
                                            >
                                                <Lock className="w-3 h-3 mr-1" />
                                                Clôturer
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            {/* Day View - Mobile */}
            <div className="lg:hidden">
                {(() => {
                    const date = weekDates[selectedDayIndex];
                    const daySchedules = weekData?.by_day?.[date] || [];
                    const dayOfWeek = getDayOfWeekFromDateString(date);
                    const dayNumber = getDayFromDateString(date);
                    
                    return (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center justify-between">
                                    <span>{FULL_DAY_NAMES[dayOfWeek]} {dayNumber}</span>
                                    <Badge variant="outline">{daySchedules.length} shift{daySchedules.length > 1 ? 's' : ''}</Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {daySchedules.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                        <p>Aucun shift planifié</p>
                                    </div>
                                ) : (
                                    daySchedules.map(schedule => {
                                        const colors = getServiceColors(schedule.staff_service);
                                        return (
                                            <div 
                                                key={schedule.id}
                                                className={`p-4 rounded-xl border-2 ${colors.bg} ${colors.border}`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-full ${colors.badge} text-white flex items-center justify-center font-bold`}>
                                                            {schedule.staff_name?.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <div className={`font-semibold ${colors.text}`}>
                                                                {schedule.staff_name}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {schedule.staff_position || schedule.staff_service}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {getStatusBadge(schedule.attendance_status)}
                                                </div>
                                                
                                                <div className="mt-3 flex items-center gap-4 text-sm">
                                                    <div className="flex items-center gap-1.5">
                                                        <Sun className="w-4 h-4 text-amber-500" />
                                                        <span className="font-mono font-medium">{schedule.start_time}</span>
                                                    </div>
                                                    <span className="text-muted-foreground">→</span>
                                                    <div className="flex items-center gap-1.5">
                                                        <Moon className="w-4 h-4 text-indigo-500" />
                                                        <span className="font-mono font-medium">{schedule.end_time}</span>
                                                    </div>
                                                    <Badge variant="outline" className="ml-auto">
                                                        <Timer className="w-3 h-3 mr-1" />
                                                        {calculateHours(schedule.start_time, schedule.end_time)}
                                                    </Badge>
                                                </div>
                                                
                                                {canEdit && (
                                                    <div className="mt-3 pt-3 border-t flex gap-2">
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm" 
                                                            className="flex-1"
                                                            onClick={() => handleEditClick(schedule)}
                                                        >
                                                            <Edit2 className="w-3 h-3 mr-1" />
                                                            Modifier
                                                        </Button>
                                                        {canDelete && (
                                                            <Button 
                                                                variant="outline" 
                                                                size="sm" 
                                                                className="text-destructive hover:text-destructive"
                                                                onClick={() => handleDelete(schedule.id)}
                                                            >
                                                                <Trash2 className="w-3 h-3" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                                
                                {canCreate && (
                                    <Button 
                                        className="w-full"
                                        onClick={() => handleAddClick(date)}
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter un shift
                                    </Button>
                                )}
                                
                                {/* Bouton clôture - mobile day view */}
                                {canCloseShift && daySchedules.length > 0 && (
                                    <Button 
                                        variant="outline"
                                        className="w-full mt-2 border-amber-200 text-amber-700 hover:bg-amber-50"
                                        onClick={() => handleOpenCloseDialog(date)}
                                    >
                                        <Lock className="w-4 h-4 mr-2" />
                                        Clôturer ce shift
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    );
                })()}
            </div>

            {/* Week View - Mobile */}
            {viewMode === 'week' && (
                <div className="lg:hidden space-y-3">
                    {weekDates.map((date, idx) => {
                        const daySchedules = weekData?.by_day?.[date] || [];
                        const dayOfWeek = getDayOfWeekFromDateString(date);
                        const dayNumber = getDayFromDateString(date);
                        const isToday = date === getGabonTodayISO();
                        
                        if (daySchedules.length === 0) return null;
                        
                        return (
                            <Card key={date} className={isToday ? 'border-primary/50 bg-primary/5' : ''}>
                                <CardHeader className="py-3 px-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                                isToday ? 'bg-primary text-primary-foreground' : 'bg-muted'
                                            }`}>
                                                {dayNumber}
                                            </div>
                                            <span className={`font-medium ${isToday ? 'text-primary' : ''}`}>
                                                {DAY_NAMES[dayOfWeek]}
                                            </span>
                                        </div>
                                        <Badge variant="outline">{daySchedules.length}</Badge>
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-0 pb-3 px-4">
                                    <div className="space-y-2">
                                        {daySchedules.map(schedule => {
                                            const colors = getServiceColors(schedule.staff_service);
                                            return (
                                                <div 
                                                    key={schedule.id}
                                                    className={`p-2 rounded-lg border ${colors.bg} ${colors.border} flex items-center justify-between`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-6 h-6 rounded-full ${colors.badge} text-white flex items-center justify-center text-xs font-bold`}>
                                                            {schedule.staff_name?.charAt(0)}
                                                        </div>
                                                        <span className={`text-sm font-medium ${colors.text}`}>
                                                            {schedule.staff_name}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-muted-foreground">
                                                            {schedule.start_time}-{schedule.end_time}
                                                        </span>
                                                        {getStatusBadge(schedule.attendance_status)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Staff Summary */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        Équipe ({staff.length} employés)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {staff.map(s => {
                            const colors = getServiceColors(s.service);
                            return (
                                <div 
                                    key={s.id} 
                                    className={`p-3 rounded-lg border ${colors.bg} ${colors.border} text-center`}
                                >
                                    <div className={`w-8 h-8 mx-auto rounded-full ${colors.badge} text-white flex items-center justify-center font-bold text-sm mb-2`}>
                                        {s.full_name?.charAt(0)}
                                    </div>
                                    <div className={`font-medium text-sm truncate ${colors.text}`}>{s.full_name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{s.position}</div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Add/Edit Dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-primary" />
                            {editSchedule ? 'Modifier le shift' : 'Nouveau shift'}
                        </DialogTitle>
                        {selectedDay && (
                            <p className="text-sm text-muted-foreground">
                                {new Date(selectedDay).toLocaleDateString('fr-FR', { timeZone: GABON_TIMEZONE, weekday: 'long', day: 'numeric', month: 'long' })}
                            </p>
                        )}
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-2">
                        {!editSchedule && (
                            <div className="space-y-2">
                                <Label>Employé *</Label>
                                <Select 
                                    value={formData.staff_id} 
                                    onValueChange={handleStaffSelect}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner un employé..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {staff.map(s => {
                                            const hasShift = checkExistingShift(s.id, selectedDay);
                                            return (
                                                <SelectItem key={s.id} value={s.id}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-2 h-2 rounded-full ${getServiceColors(s.service).badge}`}></div>
                                                        {s.full_name} 
                                                        <span className="text-muted-foreground">({s.service})</span>
                                                        {hasShift && (
                                                            <Badge variant="secondary" className="ml-2 text-[10px] h-4">
                                                                Shift existant
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1">
                                    <Sun className="w-3 h-3 text-amber-500" />
                                    Début *
                                </Label>
                                <Input 
                                    type="time" 
                                    value={formData.start_time}
                                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-1">
                                    <Moon className="w-3 h-3 text-indigo-500" />
                                    Fin *
                                </Label>
                                <Input 
                                    type="time" 
                                    value={formData.end_time}
                                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                                />
                            </div>
                        </div>
                        
                        {formData.start_time && formData.end_time && (
                            <div className="text-center p-2 bg-muted/50 rounded-lg">
                                <span className="text-sm text-muted-foreground">Durée: </span>
                                <span className="font-mono font-bold">
                                    {calculateHours(formData.start_time, formData.end_time)}
                                </span>
                            </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Service</Label>
                                <Select 
                                    value={formData.service} 
                                    onValueChange={(v) => setFormData({...formData, service: v, position: ''})}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Service..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {services.map(s => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Poste</Label>
                                <Select 
                                    value={formData.position} 
                                    onValueChange={(v) => setFormData({...formData, position: v})}
                                    disabled={!formData.service}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Poste..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(positions[formData.service] || []).map(p => (
                                            <SelectItem key={p} value={p}>{p}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Input 
                                value={formData.notes}
                                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                placeholder="Notes optionnelles..."
                            />
                        </div>
                    </div>
                    
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave}>
                            {editSchedule ? 'Modifier' : 'Créer le shift'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialogue création en lot */}
            <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CalendarDays className="w-5 h-5 text-primary" />
                            Création rapide de planning
                        </DialogTitle>
                        <p className="text-sm text-muted-foreground">
                            Créez plusieurs shifts en une seule fois
                        </p>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                        {/* Horaires */}
                        <div className="p-4 bg-muted/30 rounded-lg border">
                            <Label className="text-base font-semibold mb-3 block">Horaires</Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1 text-sm">
                                        <Sun className="w-3 h-3 text-amber-500" />
                                        Début
                                    </Label>
                                    <Input 
                                        type="time" 
                                        value={bulkData.start_time}
                                        onChange={(e) => setBulkData({...bulkData, start_time: e.target.value})}
                                        className="text-center font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1 text-sm">
                                        <Moon className="w-3 h-3 text-indigo-500" />
                                        Fin
                                    </Label>
                                    <Input 
                                        type="time" 
                                        value={bulkData.end_time}
                                        onChange={(e) => setBulkData({...bulkData, end_time: e.target.value})}
                                        className="text-center font-mono"
                                    />
                                </div>
                            </div>
                            {bulkData.start_time && bulkData.end_time && (
                                <div className="text-center mt-3 text-sm text-muted-foreground">
                                    Durée: <span className="font-mono font-bold text-foreground">
                                        {calculateHours(bulkData.start_time, bulkData.end_time)}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Sélection des jours */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-base font-semibold">Jours</Label>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={selectAllDays}
                                    className="h-7 text-xs"
                                >
                                    Tout sélectionner
                                </Button>
                            </div>
                            <div className="grid grid-cols-7 gap-2">
                                {weekDates.map((date, idx) => {
                                    const dayOfWeek = getDayOfWeekFromDateString(date);
                                    const dayNumber = getDayFromDateString(date);
                                    const isSelected = bulkData.selectedDays.includes(date);
                                    const isToday = date === getGabonTodayISO();
                                    
                                    return (
                                        <button
                                            key={date}
                                            onClick={() => toggleDaySelection(date)}
                                            className={`p-2 rounded-lg text-center transition-all border ${
                                                isSelected 
                                                    ? 'bg-primary text-primary-foreground border-primary shadow-sm' 
                                                    : isToday 
                                                        ? 'bg-primary/10 border-primary/30 hover:bg-primary/20' 
                                                        : 'bg-muted/30 hover:bg-muted border-transparent'
                                            }`}
                                        >
                                            <div className="text-xs font-medium">{DAY_NAMES[dayOfWeek]}</div>
                                            <div className="text-lg font-bold">{dayNumber}</div>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {bulkData.selectedDays.length} jour(s) sélectionné(s)
                            </p>
                        </div>

                        {/* Sélection des employés */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <Label className="text-base font-semibold">Employés</Label>
                                <div className="flex gap-2">
                                    <Select value={selectedService || "all"} onValueChange={(v) => setSelectedService(v === "all" ? "" : v)}>
                                        <SelectTrigger className="w-32 h-7 text-xs">
                                            <SelectValue placeholder="Filtrer" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Tous</SelectItem>
                                            {services.map(s => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={selectAllStaff}
                                        className="h-7 text-xs"
                                    >
                                        Tout sélectionner
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                                {(selectedService ? staff.filter(s => s.service === selectedService) : staff).map(s => {
                                    const colors = getServiceColors(s.service);
                                    const isSelected = bulkData.selectedStaff.includes(s.id);
                                    
                                    return (
                                        <button
                                            key={s.id}
                                            onClick={() => toggleStaffSelection(s.id)}
                                            className={`p-2 rounded-lg border text-left transition-all flex items-center gap-2 ${
                                                isSelected 
                                                    ? `${colors.bg} ${colors.border} ring-2 ring-primary/30` 
                                                    : 'bg-muted/30 hover:bg-muted border-transparent'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 rounded-full ${colors.badge} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                                                {s.full_name?.charAt(0)}
                                            </div>
                                            <div className="min-w-0">
                                                <div className={`text-sm font-medium truncate ${isSelected ? colors.text : ''}`}>
                                                    {s.full_name}
                                                </div>
                                                <div className="text-xs text-muted-foreground truncate">{s.service}</div>
                                            </div>
                                            {isSelected && (
                                                <div className="ml-auto">
                                                    <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                                        <span className="text-white text-xs">✓</span>
                                                    </div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {bulkData.selectedStaff.length} employé(s) sélectionné(s)
                            </p>
                        </div>

                        {/* Résumé */}
                        {bulkData.selectedStaff.length > 0 && bulkData.selectedDays.length > 0 && (
                            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                                <div className="text-sm font-medium text-primary mb-1">Récapitulatif</div>
                                <div className="text-2xl font-bold">
                                    {bulkData.selectedStaff.length * bulkData.selectedDays.length} shifts
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {bulkData.selectedStaff.length} employé(s) × {bulkData.selectedDays.length} jour(s)
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleBulkCreate}
                            disabled={bulkData.selectedStaff.length === 0 || bulkData.selectedDays.length === 0}
                            className="bg-gradient-to-r from-primary to-primary/80"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Créer {bulkData.selectedStaff.length * bulkData.selectedDays.length} shifts
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialogue de gestion des doublons */}
            <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-600">
                            <AlertCircle className="w-5 h-5" />
                            Shift existant détecté
                        </DialogTitle>
                        <DialogDescription>
                            Un shift existe déjà pour cet employé à cette date.
                        </DialogDescription>
                    </DialogHeader>
                    
                    {duplicateInfo && (
                        <div className="space-y-4 mt-4">
                            <Alert>
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                    <div className="font-semibold">{duplicateInfo.staffName}</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        {new Date(duplicateInfo.date).toLocaleDateString('fr-FR', { 
                                            timeZone: GABON_TIMEZONE,
                                            weekday: 'long', 
                                            day: 'numeric', 
                                            month: 'long' 
                                        })}
                                    </div>
                                    <div className="flex items-center gap-2 mt-2 font-mono">
                                        <Clock className="w-4 h-4 text-muted-foreground" />
                                        {duplicateInfo.schedule.start_time} - {duplicateInfo.schedule.end_time}
                                        <span className="text-muted-foreground">
                                            ({calculateHours(duplicateInfo.schedule.start_time, duplicateInfo.schedule.end_time)})
                                        </span>
                                    </div>
                                    {duplicateInfo.schedule.service && (
                                        <Badge variant="outline" className="mt-2">
                                            {duplicateInfo.schedule.service}
                                        </Badge>
                                    )}
                                </AlertDescription>
                            </Alert>
                            
                            <div className="text-sm text-muted-foreground">
                                Que souhaitez-vous faire ?
                            </div>
                            
                            <div className="grid grid-cols-1 gap-2">
                                <Button 
                                    variant="outline" 
                                    className="justify-start h-12"
                                    onClick={() => handleDuplicateAction('view')}
                                >
                                    <Eye className="w-4 h-4 mr-3 text-blue-500" />
                                    <div className="text-left">
                                        <div className="font-medium">Voir le shift</div>
                                        <div className="text-xs text-muted-foreground">Fermer et voir dans le planning</div>
                                    </div>
                                </Button>
                                
                                {canEdit && (
                                    <Button 
                                        variant="outline" 
                                        className="justify-start h-12"
                                        onClick={() => handleDuplicateAction('edit')}
                                    >
                                        <Pencil className="w-4 h-4 mr-3 text-amber-500" />
                                        <div className="text-left">
                                            <div className="font-medium">Modifier le shift</div>
                                            <div className="text-xs text-muted-foreground">Changer les horaires ou le service</div>
                                        </div>
                                    </Button>
                                )}
                                
                                {canDelete && (
                                    <Button 
                                        variant="outline" 
                                        className="justify-start h-12 border-red-200 hover:bg-red-50"
                                        onClick={() => handleDuplicateAction('delete')}
                                    >
                                        <Trash2 className="w-4 h-4 mr-3 text-red-500" />
                                        <div className="text-left">
                                            <div className="font-medium text-red-600">Supprimer le shift</div>
                                            <div className="text-xs text-muted-foreground">Libérer ce créneau</div>
                                        </div>
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="mt-4">
                        <Button variant="ghost" onClick={() => setShowDuplicateDialog(false)}>
                            Annuler
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Shift Closure Dialog */}
            <ShiftClosureDialog
                open={showCloseDialog}
                onOpenChange={setShowCloseDialog}
                date={closingDate}
                onClosed={() => fetchData()}
            />
        </div>
    );
};

export default PlanningPage;
